import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { ImageModel } from "@insite/client-framework/Types/ApiModels";
import Button, { ButtonIcon, ButtonProps } from "@insite/mobius/Button";
import Clickable from "@insite/mobius/Clickable";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import ChevronLeft from "@insite/mobius/Icons/ChevronLeft";
import ChevronRight from "@insite/mobius/Icons/ChevronRight";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { useEmblaCarousel } from "embla-carousel/react";
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

/* Valtech Custom Code */
import translate from "@insite/client-framework/Translate";
import { mediaOptions } from "@insite/mobius/utilities/breakpointMediaQueries";
import { PRODUCT } from "@store/theme/design";
import { imageStyles } from "@store/theme/mixins";
import { getCarouselArrowProps, getCarouselArrowSrc } from "@store/theme/props/carousel";
import breakpointValuesMediaQueries from "@store/utilities/breakpointValuesMediaQueries";
/* Valtech Custom Code */

interface OwnProps {
    selectedIndex?: number;
    onSelectImage?: (index: number) => void;
    extendedStyles?: ProductImageCarouselStyles;
    // Valtech Custom Code
    isVertical?: boolean;
}

const mapStateToProps = (state: ApplicationState) => ({
    productSettings: getSettingsCollection(state).productSettings,
});

type Props = OwnProps & HasProductContext & ReturnType<typeof mapStateToProps>;

export interface ProductImageCarouselStyles {
    mainContainer?: GridContainerProps;
    prevArrowGridItem?: GridItemProps;
    prevArrowButton?: ButtonProps;
    carouselGridItem?: GridItemProps;
    carouselWrapper?: InjectableCss;
    carouselContainer?: InjectableCss;
    carouselSlidesContainer?: InjectableCss;
    carouselSlide?: InjectableCss;
    carouselSlideInner?: InjectableCss;
    image?: LazyImageProps;
    selectedImage?: LazyImageProps;
    nextArrowGridItem?: GridItemProps;
    nextArrowButton?: ButtonProps;
    // Valtech Custom Code
    mainWrapper?: InjectableCss;
    innerWrapper?: InjectableCss;
    prevArrowWrapper?: InjectableCss;
    nextArrowWrapper?: InjectableCss;
}

/* Valtech Custom Code */
const thumbnailSpacing = "3px";
const arrowOverlap = "-8px";

const arrowWrapperProps: InjectableCss = {
    css: css`
        position: relative;
        z-index: 1;
        display: flex;
        justify-content: center;
        align-items: center;
    `,
};

const getImageCss = (borderColor: string) => css`
    ${props => props.theme.lazyImage?.defaultProps?.css}
    border: 1px solid ${getColor(borderColor)};
    cursor: pointer;

    img {
        padding: 3px;
    }
`;

const baseStyles: ProductImageCarouselStyles = {
    mainWrapper: {
        css: css`
            position: relative;
            height: 100%;
            width: 100%;
        `,
    },
    innerWrapper: {
        css: css`
            display: flex;
        `,
    },
    prevArrowWrapper: arrowWrapperProps,
    prevArrowButton: getCarouselArrowProps("left"),
    carouselContainer: {
        css: css`
            overflow: hidden;
        `,
    },
    carouselSlidesContainer: {
        css: css`
            display: flex;
        `,
    },
    carouselSlide: {
        css: css`
            padding: 0;
            position: relative;
            line-height: 0;
        `,
    },
    carouselSlideInner: {
        css: css`
            ${imageStyles()}
            width: 100%;
        `,
    },
    image: {
        css: getImageCss("common.border"),
    },
    selectedImage: {
        css: getImageCss("secondary"),
    },
    nextArrowWrapper: arrowWrapperProps,
    nextArrowButton: getCarouselArrowProps("right"),
};

export const productImageVerticalCarouselStyles: ProductImageCarouselStyles = {
    ...baseStyles,
    innerWrapper: {
        ...baseStyles.innerWrapper,
        css: css`
            ${baseStyles.innerWrapper?.css}
            flex-direction: column;
            text-align: center;
            width: 100%;
            max-height: 100%;

            ${({ theme }) =>
                breakpointValuesMediaQueries(
                    theme,
                    [{ values: ["100%", null, null, null, "600px"], properties: ["max-height"] }],
                    mediaOptions.min,
                )}
            ${({ theme }) =>
                breakpointValuesMediaQueries(
                    theme,
                    [{ values: ["absolute", null, "initial", null, null], properties: ["position"] }],
                    mediaOptions.min,
                )}
        `,
    },
    prevArrowButton: {
        ...baseStyles.prevArrowButton,
        icon: {
            src: getCarouselArrowSrc("top"),
            position: "left",
        },
        css: css`
            ${baseStyles.prevArrowButton?.css}
            margin-bottom: ${arrowOverlap};
        `,
    },
    carouselSlidesContainer: {
        ...baseStyles.carouselSlidesContainer,
        css: css`
            ${baseStyles.mainWrapper?.css}
            flex-direction: column;
        `,
    },
    carouselSlide: {
        ...baseStyles.carouselSlide,
        css: css`
            ${baseStyles.carouselSlide?.css}
            padding: ${thumbnailSpacing} 0;

            &:first-child {
                padding-top: 0;
            }

            &:last-child {
                padding-bottom: 0;
            }
        `,
    },
    nextArrowButton: {
        ...baseStyles.nextArrowButton,
        icon: {
            src: getCarouselArrowSrc("bottom"),
            position: "left",
        },
        css: css`
            ${baseStyles.nextArrowButton?.css}
            margin-top: ${arrowOverlap};
        `,
    },
};
/* Valtech Custom Code */

export const productImageCarouselStyles: ProductImageCarouselStyles = {
    ...baseStyles,
    mainWrapper: {
        ...baseStyles.mainWrapper,
        css: css`
            ${baseStyles.mainWrapper?.css}
            margin-top: 18px;
            height: auto;
            max-width: 600px;
        `,
    },
    innerWrapper: {
        ...baseStyles.innerWrapper,
        css: css`
            ${baseStyles.innerWrapper?.css}
            align-items: center;
            justify-content: center;
        `,
    },
    prevArrowButton: {
        ...baseStyles.prevArrowButton,
        css: css`
            ${baseStyles.prevArrowButton?.css}
            margin-right: ${arrowOverlap};
        `,
    },
    carouselSlide: {
        ...baseStyles.carouselSlide,
        css: css`
            ${baseStyles.carouselSlide?.css}
            padding: 0 ${thumbnailSpacing};

            &:first-child {
                padding-left: 0;
            }

            &:last-child {
                padding-right: 0;
            }
        `,
    },
    carouselSlideInner: {
        ...baseStyles.carouselSlideInner,
        css: css`
            ${baseStyles.carouselSlideInner?.css}
            width: 90px;
        `,
    },
    nextArrowButton: {
        ...baseStyles.nextArrowButton,
        css: css`
            ${baseStyles.nextArrowButton?.css}
            margin-left: ${arrowOverlap};
        `,
    },
};

const ProductImageCarousel = ({
    productSettings,
    productContext,
    selectedIndex,
    onSelectImage,
    extendedStyles,
    // Valtech Custom Code
    isVertical,
}: Props) => {
    /* Valtech Custom Code */
    const [styles] = useState(() =>
        mergeToNew(isVertical ? productImageVerticalCarouselStyles : productImageCarouselStyles, extendedStyles),
    );
    const minImageCount = 3;
    /* Valtech Custom Code */
    const { product } = productContext;

    const [selectedImageIndex, setSelectedImageIndex] = useState(selectedIndex);
    useEffect(() => {
        setSelectedImageIndex(selectedIndex);
    }, [selectedIndex]);

    const imageClickHandler = (index: number) => {
        setSelectedImageIndex(index);
        onSelectImage?.(index);
    };

    const [emblaRef, embla] = useEmblaCarousel();
    const [canScrollPrev, setCanScrollPrev] = useState(false);
    const [canScrollNext, setCanScrollNext] = useState(false);
    const setCanScroll = () => {
        setCanScrollPrev(!!embla && embla.canScrollPrev());
        setCanScrollNext(!!embla && embla.canScrollNext());
    };

    useEffect(() => {
        if (!embla) {
            return;
        }

        embla.on("init", setCanScroll);
        embla.on("select", setCanScroll);
    }, [embla]);

    useEffect(() => {
        if (!embla || !product.images) {
            return;
        }

        embla.reInit({
            align: "start", // Valtech Custom Code
            slidesToScroll: 1, // Valtech Custom Code
            axis: isVertical ? "y" : "x", // Valtech Custom Code
        });
        setCanScroll();
    }, [embla, product]);

    const getProductImageThumbPath = (productImage: ImageModel) => {
        if (productImage.imageType === "Static") {
            return productImage.smallImagePath || productImage.mediumImagePath;
        }
        if (productImage.imageType === "360" && productSettings.imageProvider === "SIRV") {
            return productImage.mediumImagePath + (productImage.mediumImagePath.indexOf("?") > 0 ? "&thumb" : "?thumb");
        }
        return "";
    };

    // Valtech Custom Code
    if (!product || !product.images) {
        return null;
    }

    /* Valtech Custom Code */
    const productImages = [...product.images];
    if (!productImages.length) {
        productImages.push({
            id: "unavailable",
            imageAltText: translate("Image Unavailable"),
            imageType: "Static",
            largeImagePath: PRODUCT.image.large.defaultPath,
            mediumImagePath: PRODUCT.image.medium.defaultPath,
            name: "unavailable",
            smallImagePath: PRODUCT.image.small.defaultPath,
            sortOrder: 0,
        });
    }

    return (
        <StyledWrapper {...styles.mainWrapper} key={product.id}>
            <StyledWrapper {...styles.innerWrapper}>
                <StyledWrapper {...styles.prevArrowWrapper}>
                    {productImages.length > minImageCount && (
                        <Button
                            {...styles.prevArrowButton}
                            onClick={() => embla && embla.scrollPrev()}
                            disabled={!canScrollPrev}
                        >
                            {/* Valtech Removed Code - ButtonIcon */}
                        </Button>
                    )}
                </StyledWrapper>
                <StyledWrapper {...styles.carouselContainer} ref={emblaRef}>
                    <StyledWrapper {...styles.carouselSlidesContainer}>
                        {productImages.map((productImage, index) => (
                            <StyledWrapper {...styles.carouselSlide} key={productImage.id}>
                                <StyledWrapper {...styles.carouselSlideInner}>
                                    <Clickable onClick={() => imageClickHandler(index)}>
                                        <LazyImage
                                            {...(index === selectedImageIndex ? styles.selectedImage : styles.image)}
                                            src={getProductImageThumbPath(productImage)}
                                            altText={productImage.imageAltText}
                                        />
                                    </Clickable>
                                </StyledWrapper>
                            </StyledWrapper>
                        ))}
                    </StyledWrapper>
                </StyledWrapper>
                <StyledWrapper {...styles.nextArrowWrapper}>
                    {productImages.length > minImageCount && (
                        <Button
                            {...styles.nextArrowButton}
                            onClick={() => embla && embla.scrollNext()}
                            disabled={!canScrollNext}
                        >
                            {/* Valtech Removed Code - ButtonIcon */}
                        </Button>
                    )}
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
    /* Valtech Custom Code */
};

export default connect(mapStateToProps)(withProductContext(ProductImageCarousel));
